/**
 * <AIScribeSection>
 *
 * §5 of <ZoomPanel> (`docs/ui-designs/06-telehealth-and-scribe.md`). Composes:
 *   - State-aware header (TRANSCRIBING green / PAUSED amber / AI Scribe muted).
 *   - Idle prompt: 50px coral mic icon + headline + body + coral "Start AI
 *     Scribe" button. Phase 1 (rip-out) removed the pre-scribe identity
 *     verification gate (LD-4 — patient consent already covers identity).
 *   - Streaming/paused: defers to the children prop (the parent <ZoomPanel>
 *     passes the live transcript stream + data gaps panel underneath).
 */
import { ActionIcon } from '@mantine/core'
import { IconChevronDown, IconChevronUp, IconMicrophone } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import type { ScribeState, PatientPresence } from '@/components/session/callSessionReducer'
import { scribeText, SCRIBE_ACCENT } from './scribeText'

export type AIScribeSectionProps = {
  scribeState: ScribeState
  /** Patient presence — gates TRANSCRIBING vs "Scribe Ready" copy (PD-1067). */
  patientPresence: PatientPresence
  /**
   * True once the live transcript has captured its first words — i.e. the
   * conversation has actually started. Gates TRANSCRIBING so it reflects real
   * capture, not merely the patient being in the room (PD-1067 item 5).
   */
  conversationStarted: boolean
  isStarting: boolean
  /** Click handler for the coral Start button (idle state). */
  onStart: () => void
  /**
   * Whether the local user may start the scribe (true only for the treating
   * physician). When false the idle Start button is hidden. Defaults true.
   */
  canStart?: boolean
  /**
   * Children rendered below the section header when the scribe is
   * streaming or paused. The parent <ZoomPanel> passes the Data Gaps panel
   * + transcript stream in here.
   */
  children?: ReactNode
  /**
   * Collapses the transcript body to just the header, releasing the rail's
   * vertical space (to Visit Guidance). No effect while idle — the
   * start-scribe prompt has nothing to collapse.
   */
  collapsed: boolean
  onToggleCollapsed: () => void
}

export function AIScribeSection({
  scribeState,
  patientPresence,
  conversationStarted,
  isStarting,
  onStart,
  canStart = true,
  children,
  collapsed,
  onToggleCollapsed,
}: AIScribeSectionProps) {
  const isStreaming = scribeState === 'streaming'
  const isPaused = scribeState === 'paused'
  const isIdle = scribeState === 'idle'
  const isConnected = patientPresence === 'connected'
  // Scribe auto-starts on provider join, so it can be 'streaming' before anyone
  // speaks. Only call it TRANSCRIBING once the patient is in the room AND the
  // conversation has started (first words captured) — otherwise it is running
  // but idle, so show "Scribe Ready" (PD-1067 item 5). Display gate only; the
  // capture keeps running underneath. The sub-text names what we're waiting on.
  const isTranscribing = isStreaming && isConnected && conversationStarted
  const readySubtext = isConnected
    ? '— listening, transcription begins when the conversation starts'
    : '— transcription begins when patient joins'

  const isCollapsed = collapsed && !isIdle

  return (
    <div
      data-testid="zoom-panel-scribe-section"
      className={`flex flex-col overflow-hidden ${isCollapsed ? 'shrink-0' : 'min-h-0 flex-1'}`}
    >
      {/* Section header — toggles the transcript body while one exists. */}
      <div
        className={`flex shrink-0 items-center justify-between border-t border-white/[0.07] px-[14px] py-[9px] ${
          isIdle ? '' : 'cursor-pointer select-none'
        }`}
        onClick={isIdle ? undefined : onToggleCollapsed}
      >
        <div className="flex items-center gap-[6px]">
          {isTranscribing ? (
            <>
              <span aria-hidden="true" className="h-[7px] w-[7px] rounded-full bg-[#22c55e]" />
              <span className={`${scribeText.eyebrow} text-[#22c55e]`}>TRANSCRIBING</span>
            </>
          ) : isStreaming ? (
            <>
              <span aria-hidden="true" className="h-[7px] w-[7px] rounded-full bg-[#f59e0b]" />
              <span className={`${scribeText.eyebrow} text-[#f59e0b]`}>SCRIBE READY</span>
              <span className={`${scribeText.meta} text-slate-500`}>{readySubtext}</span>
            </>
          ) : isPaused ? (
            <>
              <span aria-hidden="true" className="h-[7px] w-[7px] rounded-full bg-[#f59e0b]" />
              <span className={`${scribeText.eyebrow} text-[#f59e0b]`}>PAUSED</span>
            </>
          ) : (
            <span className={`${scribeText.eyebrow} text-slate-600`}>AI Scribe</span>
          )}
        </div>
        {!isIdle && (
          <ActionIcon
            variant="subtle"
            size="xs"
            color="gray"
            aria-label={collapsed ? 'Expand transcript' : 'Collapse transcript'}
            onClick={(e) => {
              e.stopPropagation()
              onToggleCollapsed()
            }}
          >
            {collapsed ? <IconChevronDown size={14} /> : <IconChevronUp size={14} />}
          </ActionIcon>
        )}
      </div>

      {/* Body — children when active, idle prompt when not.
          The idle body must scroll vertically: on short viewports (or when
          the §1–§4 stack consumes most of the panel) the Start Scribe button
          sits below the fold and the user had no way to reach it. The
          outer <aside> is overflow:hidden + position:fixed, so the scroll
          MUST live one level in — `min-h-0` lets flex-1 actually shrink to
          fit, and `overflow-y-auto` exposes the scrollbar. */}
      {isIdle ? (
        <div
          data-testid="zoom-panel-scribe-idle"
          className="flex min-h-0 flex-1 flex-col items-center justify-start gap-[14px] overflow-y-auto p-4"
        >
          <div
            className="flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-full"
            style={{
              background: `${SCRIBE_ACCENT}1a`,
              border: `1px solid ${SCRIBE_ACCENT}25`,
            }}
          >
            <IconMicrophone size={22} color={SCRIBE_ACCENT} strokeWidth={1.8} />
          </div>
          <div className="shrink-0 text-center">
            <div className={`mb-1 ${scribeText.label} text-slate-200`}>AI Scribe</div>
            <div className={`${scribeText.body} leading-relaxed text-slate-600`}>
              {canStart
                ? 'Real-time transcription and structured note capture.'
                : 'Available to the treating physician on this visit.'}
            </div>
          </div>
          {canStart && (
            <button
              type="button"
              onClick={onStart}
              disabled={isStarting}
              data-testid="zoom-panel-start-scribe"
              className={`flex shrink-0 items-center gap-[7px] rounded-lg border-0 px-5 py-[9px] ${scribeText.label} text-white disabled:cursor-not-allowed disabled:opacity-60`}
              style={{ background: SCRIBE_ACCENT }}
            >
              <IconMicrophone size={13} />
              {isStarting ? 'Starting…' : 'Start AI Scribe'}
            </button>
          )}
        </div>
      ) : isCollapsed ? null : (
        children
      )}
    </div>
  )
}
